if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.bindVariantSelect();
      }

      bindVariantSelect() {
        const variantsJson = this.querySelector('.product-variants-json');
        if (!variantsJson) return;

        const variants = JSON.parse(variantsJson.textContent);
        const variantSelects = this.closest('product-info')?.querySelector('variant-selects');
        if (!variantSelects) return;

        variantSelects.addEventListener('change', () => {
          const inputs = Array.from(variantSelects.querySelectorAll('.product-form__input'));
          const selected = inputs.map((group) => {
            const radio = group.querySelector('input[type="radio"]:checked');
            const select = group.querySelector('select');
            return radio ? radio.value : select?.value;
          });

          const allSelected = selected.every(Boolean) && selected.length === inputs.length;
          const match = variants.find((v) => v.options.every((opt, i) => opt === selected[i]));

          if (match) {
            this.variantIdInput.value = String(match.id);

            if (allSelected) {
              const submitButtonText = this.submitButtonText;
              if (!submitButtonText) return;
              if (!match.available) {
                submitButtonText.textContent = window.variantStrings.soldOut;
                this.submitButton.setAttribute('disabled', 'disabled');
              } else {
                submitButtonText.textContent = window.variantStrings.addToCart;
                this.submitButton.removeAttribute('disabled');
                this.submitButton.removeAttribute('aria-disabled');
              }
            }
          }
        });
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        const missingOptions = this.allOptionsSelected();
        if (missingOptions) {
          this.handleErrorMessage(`Please select an option from ${missingOptions.join(' and ')} before adding to cart.`);
          return;
        }

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      allOptionsSelected() {
        const selectors = this.closest('product-info')?.querySelector('variant-selects');
        if (!selectors) return null;

        const inputs = selectors.querySelectorAll('.product-form__input');
        if (inputs.length === 0) return null;

        const missing = [];

        for (const input of inputs) {
          const hasChecked = input.querySelector('input[type="radio"]:checked');
          if (!hasChecked) {
            const label = input.querySelector('legend.form__label')?.textContent?.trim();
            missing.push(label || 'option');
          }
        }

        return missing.length > 0 ? missing : null;
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}

document.addEventListener('DOMContentLoaded', () => {
  if (!new URLSearchParams(window.location.search).has('variant')) {
    document.querySelectorAll('product-form').forEach(form => {
      const selectors = form.closest('product-info')?.querySelector('variant-selects');
      const totalOptions = selectors?.querySelectorAll('.product-form__input').length ?? 0;
      if (totalOptions > 0) {
        form.querySelector('input[name="id"]').value = '';
      }
    });
  }
});